<template>
    <div class="automation-wizard-condition-row">
        <div class="automation-wizard-condition-row__content">
            <div class="automation-wizard-condition-row__header">
                <CompassColoredIcon
                    icon="filter"
                    :color="CompassEnums.ColoredIconColorType.PURPLE"
                />
                <span
                    class="automation-wizard-condition-row__title typographies-paragraph-caps-small-bold"
                >
                    {{ translations.configureTitle }}
                </span>
                <CompassIconButton
                    icon="bin"
                    :type="CompassEnums.IconButtonType.SECONDARY"
                    :variant="CompassEnums.IconButtonVariant.DESTRUCTIVE"
                    @click="emit('remove')"
                />
            </div>
            <div class="automation-wizard-condition-row__inline-row">
                <span
                    class="automation-wizard-condition-row__if-label typographies-paragraph-medium-bold"
                >
                    {{ translations.ifLabel }}
                </span>
                <CompassSelect
                    :modelValue="condition.id || ''"
                    filterable
                    menuClass="select-automation-wizard"
                    :placeholder="translations.fieldPlaceholder"
                    @update:modelValue="handleFieldChange"
                >
                    <CompassOptionGroup
                        v-for="group in groupedFields"
                        :key="group.label"
                        :label="group.label"
                    >
                        <CompassDropdownOption
                            v-for="field in group.fields"
                            :key="field.id"
                            :label="field.name"
                            :labelCaption="field.isProperty ? '' : translations.customFieldCaption"
                            :value="field.id"
                        />
                    </CompassOptionGroup>
                </CompassSelect>
            </div>
            <div
                v-if="condition.id"
                :key="condition.id"
                class="automation-wizard-condition-row__fields"
                :class="{
                    'automation-wizard-condition-row__fields--inline': isInlineValueLayout,
                }"
            >
                <CompassRadioGroup
                    v-if="isBoolean"
                    :modelValue="condition.value"
                    label=""
                    direction="horizontal"
                    @update:modelValue="handleBooleanChange"
                >
                    <CompassRadio :label="translations.booleanYes" modelValue="true" />
                    <CompassRadio :label="translations.booleanNo" modelValue="false" />
                </CompassRadioGroup>
                <template v-else>
                    <CompassSelect
                        :modelValue="condition.operator"
                        menuClass="select-automation-wizard"
                        :placeholder="translations.operatorPlaceholder"
                        @update:modelValue="handleOperatorChange"
                    >
                        <CompassDropdownOption
                            v-for="op in availableOperators"
                            :key="op.value"
                            :label="op.label"
                            :value="op.value"
                        />
                    </CompassSelect>
                    <template v-if="condition.operator && !isValueLessOperator">
                        <!-- Date -->
                        <CompassDatePicker
                            v-if="isDate"
                            :modelValue="parsedValue || ''"
                            @update:modelValue="handleValueChange"
                        />
                        <!-- Select (single) -->
                        <CompassSelect
                            v-else-if="isSelect"
                            :modelValue="parsedValue || ''"
                            menuClass="select-automation-wizard"
                            :placeholder="translations.valuePlaceholder"
                            @update:modelValue="handleValueChange"
                        >
                            <CompassDropdownOption
                                v-for="item in selectedCfItems"
                                :key="item.id"
                                :label="item.label"
                                :value="item.id"
                            />
                        </CompassSelect>
                        <!-- Checkbox -->
                        <div
                            v-else-if="isCheckbox"
                            class="automation-wizard-condition-row__checkboxes"
                        >
                            <CompassCheckbox
                                v-for="item in selectedCfItems"
                                :key="item.id"
                                :modelValue="item.id"
                                :label="item.label"
                                :checked="isCheckboxItemSelected(item.id)"
                                @change="toggleCheckboxValue(item.id)"
                            />
                        </div>
                        <!-- Numeric -->
                        <CompassInput
                            v-else-if="isNumeric"
                            :modelValue="parsedValue || ''"
                            :type="CompassEnums.InputType.NUMBER"
                            :placeholder="translations.valuePlaceholder"
                            @update:modelValue="handleValueChange"
                        >
                            <template v-if="showCurrencyPrefix" #prefix>
                                <span>{{ numericSymbol }}</span>
                            </template>
                            <template v-if="showCurrencySuffix" #suffix>
                                <span>{{ numericSymbol }}</span>
                            </template>
                        </CompassInput>
                        <!-- Simple text -->
                        <CompassInput
                            v-else
                            :modelValue="parsedValue || ''"
                            :placeholder="translations.valuePlaceholder"
                            @update:modelValue="handleValueChange"
                        />
                    </template>
                </template>
            </div>
            <Transition name="fade-down">
                <CompassAlert
                    v-if="isFieldUnavailable"
                    :type="CompassEnums.AlertType.WARNING"
                    :description="translations.cfUnavailable"
                    :showClose="false"
                />
                <CompassAlert
                    v-else-if="isConditionComplete"
                    :type="CompassEnums.AlertType.SUCCESS"
                    :description="conditionSummary"
                    :showClose="false"
                />
            </Transition>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { useStore } from 'vuex';
import { Translator } from 'types/injects/translator.type';
import {
    CF_TYPE_OPERATORS,
    ConditionOperator,
    ConditionScope,
    PROPERTY_TYPE_TO_CF_TYPE,
} from 'Dictionaries/automation/conditionOperators';
import { escapeHtml } from 'Helpers/htmlSanitizeHelper.js';
import { formatToDisplayWithCurrency } from 'utils/numberFormat';
import type {
    CfItem,
    Condition,
    ConditionField,
    FieldGroup,
    OperatorOption,
} from 'vcomponents/automation/templates/Wizard/Condition/types';

interface Props {
    groupedFields: FieldGroup[];
}

const props = defineProps<Props>();

const condition = defineModel<Condition>({ required: true });

const emit = defineEmits<{
    remove: [];
}>();

const $t = inject('$t') as Translator;

const store = useStore();

const translations: Record<string, string> = {
    configureTitle: $t.__('automation.AutomationWizardCondition.configure_title'),
    ifLabel: $t.__('common.labels.if'),
    fieldPlaceholder: $t.__('common.labels.select_field'),
    customFieldCaption: $t.__('common.labels.customfield'),
    operatorPlaceholder: $t.__('common.labels.select_condition'),
    valuePlaceholder: $t.__('common.labels.enter_value'),
    booleanYes: $t.__('common.labels.yes'),
    booleanNo: $t.__('common.labels.no'),
    cfUnavailable: $t.__('automation.AutomationWizardCondition.cf_unavailable'),
};

const allFields = computed((): ConditionField[] =>
    props.groupedFields.flatMap((group) => group.fields),
);

const selectedField = computed((): ConditionField | undefined =>
    allFields.value.find((field) => field.id === condition.value.id),
);

const isProperty = computed(
    (): boolean => condition.value.scope === ConditionScope.OBJECT_PROPERTIES,
);

const isFieldUnavailable = computed((): boolean => !!condition.value.id && !selectedField.value);

const dataType = computed((): string | null => condition.value.dataType || null);

const resolvedDataType = computed((): string | null => {
    const dt = dataType.value;
    if (!dt) {
        return null;
    }
    return PROPERTY_TYPE_TO_CF_TYPE[dt] || dt;
});

const selectedCfItems = computed((): CfItem[] => selectedField.value?.parameters?.items || []);

const isDate = computed((): boolean => resolvedDataType.value === 'date');
const isSelect = computed((): boolean => resolvedDataType.value === 'select');
const isCheckbox = computed((): boolean => resolvedDataType.value === 'checkbox');
const isNumeric = computed((): boolean => resolvedDataType.value === 'numeric');
const isBoolean = computed((): boolean => resolvedDataType.value === 'boolean');
const isInlineValueLayout = computed((): boolean => isDate.value || isNumeric.value);

const isValueLessOperator = computed<boolean>(
    () =>
        condition.value.operator === ConditionOperator.EMPTY ||
        condition.value.operator === ConditionOperator.NOT_EMPTY,
);

const numformat = computed(() => store.state.corp.numformat);

const currencySymbol = computed((): string | null => {
    if (dataType.value === 'amount') {
        return numformat.value?.currencysymbol || '€';
    }
    return null;
});

const showCurrencyPrefix = computed((): boolean => {
    if (!currencySymbol.value) {
        return false;
    }
    const pos = numformat.value?.currencypos;
    return pos === 'left' || pos === 'both';
});

const showCurrencySuffix = computed((): boolean => {
    if (dataType.value === 'percent') {
        return true;
    }
    if (!currencySymbol.value) {
        return false;
    }
    const pos = numformat.value?.currencypos;
    return pos === 'right' || pos === 'both' || !pos;
});

const numericSymbol = computed((): string | null => {
    if (dataType.value === 'percent') {
        return '%';
    }
    return currencySymbol.value;
});

const parsedValue = computed((): string | string[] | null => {
    const rawValue = condition.value.value;
    if (rawValue === null || rawValue === undefined) {
        return null;
    }
    if (isCheckbox.value) {
        try {
            const parsed = JSON.parse(rawValue);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return rawValue;
});

const serializeValue = (value: string | string[]): string => {
    if (isCheckbox.value && Array.isArray(value)) {
        return JSON.stringify(value);
    }
    return value as string;
};

const availableOperators = computed((): OperatorOption[] => {
    if (!resolvedDataType.value) {
        return [];
    }
    const operators = CF_TYPE_OPERATORS[resolvedDataType.value] || [];
    return operators.map((operator) => ({
        value: operator,
        label: $t.__(`common.operators.${operator}`),
    }));
});

const isConditionComplete = computed((): boolean => {
    if (!condition.value.id || !condition.value.operator) {
        return false;
    }
    if (isValueLessOperator.value) {
        return true;
    }
    const value = condition.value.value;
    if (value === null || value === undefined || value === '') {
        return false;
    }
    if (isCheckbox.value) {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) && parsed.length > 0;
        } catch {
            return false;
        }
    }
    return true;
});

const formatDisplayValue = (value: string): string => {
    if (isBoolean.value) {
        return value === 'true' ? translations.booleanYes : translations.booleanNo;
    }
    if (isDate.value) {
        const dateFormat = store.state.corp.timeformat === 'dmy' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
        return moment(value).format(dateFormat);
    }
    if (isSelect.value) {
        const items = selectedCfItems.value;
        const item = items.find((cfItem) => String(cfItem.id) === String(value));
        return item ? item.label : value;
    }
    if (numericSymbol.value) {
        if (dataType.value === 'amount' && numformat.value) {
            const formatted = formatToDisplayWithCurrency(parseFloat(value), numformat.value, true);
            if (formatted !== false) {
                return String(formatted);
            }
        }
        return `${value} ${numericSymbol.value}`;
    }
    if (isCheckbox.value) {
        const items = selectedCfItems.value;
        try {
            const parsed = JSON.parse(value);
            if (!Array.isArray(parsed)) {
                return value;
            }
            return parsed
                .map((itemId: string) => {
                    const item = items.find((cfItem) => String(cfItem.id) === String(itemId));
                    return item ? item.label : itemId;
                })
                .join(', ');
        } catch {
            return value;
        }
    }
    return value;
};

const conditionSummary = computed((): string => {
    if (!isConditionComplete.value || !selectedField.value) {
        return '';
    }
    const cfName = isProperty.value
        ? condition.value.label || selectedField.value.name
        : selectedField.value.name;
    const operatorLabel = $t.__(`common.operators.${condition.value.operator}`);

    if (isValueLessOperator.value) {
        const summaryKey = isProperty.value
            ? 'automation.AutomationWizardCondition.summary_property_no_value'
            : 'automation.AutomationWizardCondition.summary_no_value';
        return $t.__(summaryKey, { cfName, operator: operatorLabel });
    }

    const valueDisplay = escapeHtml(formatDisplayValue(condition.value.value!));
    const summaryKey = isProperty.value
        ? 'automation.AutomationWizardCondition.summary_property'
        : 'automation.AutomationWizardCondition.summary';
    return $t.__(summaryKey, {
        cfName,
        operator: operatorLabel,
        value: valueDisplay,
    });
});

const getDefaultValueForType = (effectiveType: string | null): string | null => {
    if (effectiveType === 'date') {
        return moment().format('YYYY-MM-DD');
    }
    if (effectiveType === 'boolean') {
        return 'true';
    }
    return null;
};

const handleFieldChange = (fieldId: string): void => {
    const field = allFields.value.find((item) => item.id === fieldId);
    if (!field) {
        return;
    }

    const fieldType = field.type;
    const effectiveType = PROPERTY_TYPE_TO_CF_TYPE[fieldType] || fieldType;
    const operators = CF_TYPE_OPERATORS[effectiveType] || [];
    const defaultOperator = operators[0] || null;

    condition.value = {
        ...condition.value,
        scope: field.isProperty ? ConditionScope.OBJECT_PROPERTIES : ConditionScope.CUSTOM_FIELDS,
        id: fieldId,
        dataType: fieldType,
        operator: defaultOperator,
        value: getDefaultValueForType(effectiveType),
        ...(field.isProperty
            ? { label: field.name, group: field.group }
            : { label: undefined, group: undefined }),
    };
};

const handleOperatorChange = (operator: string): void => {
    const wasValueLess = isValueLessOperator.value;
    const willBeValueLess =
        operator === ConditionOperator.EMPTY || operator === ConditionOperator.NOT_EMPTY;

    let value = condition.value.value;
    if (willBeValueLess) {
        value = null;
    } else if (wasValueLess) {
        value = getDefaultValueForType(resolvedDataType.value);
    }

    condition.value = { ...condition.value, operator, value };
};

const handleValueChange = (value: string | string[]): void => {
    condition.value = { ...condition.value, value: serializeValue(value) };
};

const handleBooleanChange = (boolValue: string): void => {
    condition.value = { ...condition.value, value: boolValue };
};

const isCheckboxItemSelected = (itemId: string): boolean => {
    const currentValues = parsedValue.value;
    if (!Array.isArray(currentValues)) {
        return false;
    }
    return currentValues.some((value) => String(value) === String(itemId));
};

const toggleCheckboxValue = (itemId: string): void => {
    const currentValues = Array.isArray(parsedValue.value) ? [...parsedValue.value] : [];
    const index = currentValues.findIndex((value) => String(value) === String(itemId));
    if (index >= 0) {
        currentValues.splice(index, 1);
    } else {
        currentValues.push(itemId);
    }
    handleValueChange(currentValues);
};
</script>

<style lang="scss" scoped>
.automation-wizard-condition-row {
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23B690FF' stroke-width='2' stroke-dasharray='3,7' stroke-dashoffset='4' stroke-linecap='square'/%3e%3c/svg%3e");
    background-origin: content-box;
    background-repeat: no-repeat;
    border-radius: var(--spacing-2);
    padding: 1px;
    &__content {
        padding: var(--spacing-3);
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
    }
    &__header {
        display: flex;
        align-items: center;
        gap: var(--spacing-1);
    }
    &__title {
        flex: 1;
        color: var(--colors-common-text-subtle-default);
    }
    &__inline-row {
        display: flex;
        align-items: center;
        gap: var(--spacing-1);
        > :last-child {
            flex: 1;
            min-width: 0;
        }
    }
    &__if-label {
        flex-shrink: 0;
    }
    &__fields {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
        :deep(.compass-select),
        :deep(.compass-input),
        :deep(.compass-datepicker) {
            width: 100%;
        }
        &--inline {
            flex-direction: row;

            > :deep(*) {
                flex: 1;
                min-width: 0;
            }
        }
    }
    &__checkboxes {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-2);
    }
    :deep(.compass-alert) {
        margin: 0;
        overflow-wrap: anywhere;
    }
}

.fade-down-enter-active {
    transition:
        opacity 0.3s ease,
        transform 0.3s ease;
}

.fade-down-leave-active {
    transition:
        opacity 0.2s ease,
        transform 0.2s ease;
}

.fade-down-enter-from {
    opacity: 0;
    transform: translateY(-8px);
}

.fade-down-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}
</style>
